import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Box, Download, Footprints, Pause, Play } from "lucide-react";
import { BaoRig } from "../game/bao/BaoRig";
import { ANIMS, NEUTRAL, duration, exportRig, poseFromPatient, sample } from "../game/bao/rig";
import { CHARACTERS } from "../game/bao/characters";
import { CastPicker } from "../components/game/CastPicker";
import { Logo } from "../components/brand/Logo";
import { ThemeToggle } from "../components/brand/Experience";

const download = (name, text, type) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type }));
  a.download = name; a.click();
};

const useClock = (playing, speed) => {
  const [t, setT] = useState(0);
  useEffect(() => {
    if (!playing) return;
    let raf, last = performance.now();
    const loop = (now) => { setT((x) => x + ((now - last) / 1000) * speed); last = now; raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [playing, speed]);
  return t;
};

const Card = ({ children, className = "", testId }) => <section data-testid={testId} className={`shadow-soft rounded-[2rem] border border-border/70 bg-card p-5 sm:p-6 ${className}`}>{children}</section>;
const Label = ({ children }) => <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary-strong" style={{ fontFamily: "Manrope" }}>{children}</p>;

export default function BaoStudio() {
  const nav = useNavigate();
  const [who, setWho] = useState("bao");
  const ch = CHARACTERS[who];
  const [anim, setAnim] = useState("walk");
  const [view, setView] = useState(ANIMS.walk.view);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [drive, setDrive] = useState(0.5);
  const [driveEx, setDriveEx] = useState("knee");
  const t = useClock(playing, speed);
  const sheet = useRef(null);
  const pick = (k) => { setAnim(k); setView(ANIMS[k].view); };

  return (
    <div className="baobab-watermark min-h-[100dvh] bg-background" data-testid="bao-studio">
      <header className="safe-top bg-navy-depth flex items-center justify-between px-4 pb-3 text-white sm:px-8">
        <div className="flex items-center gap-3">
          <button data-testid="studio-back" onClick={() => nav("/")} aria-label="Retour" className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10"><ArrowLeft className="h-5 w-5" /></button>
          <Logo size={34} /><div className="leading-tight"><p className="font-display text-lg font-semibold">Studio des personnages</p><p className="text-[10px] font-bold tracking-[0.25em] text-[#4FD1C5]">PERSONNAGE · RIG · ANIMATIONS</p></div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="studio-open-3d" onClick={() => nav("/bao/3d")} className="flex h-11 items-center gap-2 rounded-full bg-[#0A2540] px-4 text-sm font-extrabold text-white"><Box className="h-4 w-4" />Bao 3D</button>
          <button data-testid="studio-play-savanna" onClick={() => nav("/patient/savanna")} className="flex h-11 items-center gap-2 rounded-full bg-[#E0B33A] px-4 text-sm font-extrabold text-[#0A2540]"><Footprints className="h-4 w-4" /><span className="hidden sm:inline">Jouer la Savane</span><span className="sm:hidden">Savane</span></button>
          <ThemeToggle />
        </div>
      </header>
      <main className="mx-auto max-w-6xl space-y-6 p-4 sm:p-8">
        <p data-testid="not-medical-device" className="inline-block rounded-full bg-amber-100 px-3 py-1.5 text-xs font-bold text-amber-800">Outil d'aide à la rééducation — ce n'est pas un dispositif médical.</p>
        <CastPicker value={who} onChange={setWho} />
        <div className="grid gap-6 lg:grid-cols-5">
          <Card className="lg:col-span-2" testId="turnaround">
            <Label>Planche de référence</Label>
            <h1 className="mt-1 font-display text-3xl font-semibold" data-testid="turnaround-title">{ch.name}</h1><p className="text-sm text-muted-foreground">{ch.role}</p>
            <div className="mt-4 grid grid-cols-3 gap-1 rounded-3xl bg-gradient-to-b from-[#EAF7F5] to-[#F6E9CF] p-2 dark:from-[#0F3A5F] dark:to-[#0A2540]">
              {[["front", "Face"], ["profile", "Profil"], ["back", "Dos"]].map(([v, l]) => (
                <figure key={v}><BaoRig pose={NEUTRAL} view={v} width="100%" character={who} testId={`bao-${v}`} /><figcaption className="text-center text-xs font-bold text-muted-foreground">{l}</figcaption></figure>))}
            </div>
            <figure className="mt-3 overflow-hidden rounded-3xl border border-border/70" data-testid="illustrated-sheet">
              <img decoding="async" src={ch.sheet} alt={`Planche illustrée de ${ch.name} : face, profil, dos`} className="aspect-[3/2] w-full object-cover" loading="lazy" />
              <figcaption className="bg-muted px-3 py-2 text-xs font-bold text-muted-foreground">Planche illustrée · rendu 3D de référence</figcaption>
            </figure>
            <p className="mt-3 text-xs text-muted-foreground">Même squelette que Bao pour tout le casting : cuisses et mollets galbés, genoux et chevilles articulés, pieds complets (talon, voûte, orteils) ou chaussures de rando à semelle crantée. Le contact au sol est recalculé à chaque image.</p>
          </Card>
          <Card className="lg:col-span-3" testId="anim-player">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><Label>Animation</Label><p className="font-display text-2xl font-semibold" data-testid="anim-label">{ANIMS[anim].label}</p><p className="text-xs text-muted-foreground">{ANIMS[anim].clinical}</p></div>
              <div className="flex gap-2">
                {["profile", "front", "back"].map((v) => <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)} className={`h-11 rounded-full px-4 text-sm font-bold ${view === v ? "bg-brand text-white" : "bg-muted"}`}>{{ front: "Face", profile: "Profil", back: "Dos" }[v]}</button>)}
              </div>
            </div>
            <div className="mt-3 flex justify-center rounded-3xl bg-gradient-to-b from-[#CFEFEA] via-[#F3EBD3] to-[#E2C58F] dark:from-[#0F3A5F] dark:via-[#13324F] dark:to-[#3A2C1A]">
              <BaoRig pose={sample(anim, t)} view={view} width={300} character={who} testId="bao-live" />
            </div>
            <div className="mt-4 flex flex-wrap gap-2" role="radiogroup" aria-label="Animations">
              {Object.entries(ANIMS).map(([k, a]) => (
                <button key={k} role="radio" aria-checked={anim === k} data-testid={`anim-${k}`} onClick={() => pick(k)}
                  className={`h-11 rounded-full px-4 text-sm font-semibold transition-colors ${anim === k ? "bg-[#0A2540] text-white dark:bg-[#E0B33A] dark:text-[#0A2540]" : "bg-muted hover:bg-muted/70"}`}>{a.label}</button>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button data-testid="anim-play-toggle" onClick={() => setPlaying(!playing)} aria-label={playing ? "Pause" : "Lecture"} className="flex h-11 w-11 items-center justify-center rounded-full bg-[#E0B33A] text-[#0A2540]">{playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}</button>
              {[0.5, 1].map((s) => <button key={s} data-testid={`speed-${s}`} onClick={() => setSpeed(s)} className={`h-11 rounded-full px-4 text-sm font-bold ${speed === s ? "bg-brand text-white" : "bg-muted"}`}>{s === 0.5 ? "Lent ×0,5" : "Normal"}</button>)}
            </div>
          </Card>
        </div>
        <Card testId="patient-drive">
          <Label>Pilotage par le patient (aperçu de la vague #3)</Label>
          <p className="mt-1 text-sm text-muted-foreground">Le mouvement mesuré du patient (0 → 100 % de son amplitude sûre) fait bouger Bao.</p>
          <div className="mt-4 grid items-center gap-4 sm:grid-cols-[1fr_220px]">
            <div>
              <div className="flex flex-wrap gap-2">{[["knee", "Montée de genou"], ["step", "Pas"], ["shift", "Transfert"], ["balance", "Équilibre"]].map(([k, l]) => (
                <button key={k} data-testid={`drive-${k}`} onClick={() => setDriveEx(k)} className={`h-11 rounded-full px-4 text-sm font-semibold ${driveEx === k ? "bg-brand text-white" : "bg-muted"}`}>{l}</button>))}</div>
              <label className="mt-4 block text-sm font-semibold">Amplitude du patient : <span data-testid="drive-value">{Math.round(drive * 100)} %</span>
                <input data-testid="drive-slider" type="range" min="0" max="1" step="0.01" value={drive} onChange={(e) => setDrive(Number(e.target.value))} className="mt-2 h-10 w-full accent-[#17A2A2]" />
              </label>
            </div>
            <div className="flex justify-center rounded-3xl bg-muted"><BaoRig pose={poseFromPatient(driveEx, drive)} view={["shift", "balance"].includes(driveEx) ? "front" : "profile"} width={200} character={who} testId="bao-driven" /></div>
          </div>
        </Card>
        <Card testId="sprite-sheet">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div><Label>Planche d'animation</Label><p className="font-display text-2xl font-semibold">Sprite sheet · 8 images par mouvement</p></div>
            <div className="flex gap-2">
              <button data-testid="download-sheet" onClick={() => download(`${who}-sprite-sheet.svg`, sheet.current.outerHTML, "image/svg+xml")} className="flex h-11 items-center gap-2 rounded-full bg-muted px-4 text-sm font-bold"><Download className="h-4 w-4" />Planche SVG</button>
              <button data-testid="download-rig" onClick={() => download(`${who}-rig.json`, JSON.stringify({ ...exportRig(), name: ch.name, character: who, costume: { hair: ch.hair, top: ch.top, footwear: ch.footwear, extras: ch.extras, scale: ch.scale, build: ch.build } }, null, 2), "application/json")} className="bg-brand flex h-11 items-center gap-2 rounded-full px-4 text-sm font-bold text-white"><Download className="h-4 w-4" />Rig JSON (Unity)</button>
            </div>
          </div>
          <ol className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-muted-foreground" data-testid="sheet-legend">
            {Object.values(ANIMS).map((a, i) => <li key={a.label} className="rounded-full bg-muted px-3 py-1">Ligne {i + 1} · {a.label} ({a.view === "front" ? "face" : "profil"})</li>)}
          </ol>
          <div className="mt-4 overflow-x-auto">
            <svg ref={sheet} xmlns="http://www.w3.org/2000/svg" viewBox={`0 0 ${8 * 150} ${Object.keys(ANIMS).length * 280}`} className="w-[1000px] max-w-none sm:w-full">
              {Object.entries(ANIMS).map(([k, a], row) => (
                <g key={k} transform={`translate(0 ${row * 280})`}>
                  {Array.from({ length: 8 }, (_, i) => (
                    <g key={i} transform={`translate(${i * 150} 38) scale(0.5)`}>
                      <rect x="6" y="6" width="288" height="448" rx="24" fill="#EAF7F5" opacity=".55" />
                      <BaoRig pose={sample(k, (duration(k) * i) / 8)} view={a.view} width={300} character={who} shadow />
                    </g>
                  ))}
                </g>
              ))}
            </svg>
          </div>
        </Card>
      </main>
    </div>
  );
}
